import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { OFFERING_LINKS, NAV_LINKS } from "../config/site";

export function BrandLogo({ size = "h-9 md:h-10" }) {
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0 group" aria-label="Kalki AI Fund — Home">
      <img
        src="/kalki-wordmark.png"
        alt="Kalki AI Fund full logo — gold shield emblem with KALKI AI wordmark"
        className={`${size} w-auto mix-blend-screen transition-transform duration-500 group-hover:scale-[1.03]`}
      />
    </Link>
  );
}

const linkCls = ({ isActive }) =>
  `text-[13px] font-medium tracking-wide transition-colors duration-300 ${
    isActive ? "text-gold-300" : "text-zinc-400 hover:text-gold-200"
  }`;

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [drop, setDrop] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
    setDrop(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-2xl border-b border-gold-400/12"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex items-center justify-between h-[76px]">
            <BrandLogo />

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop">
              <NavLink to="/" data-testid="nav-link-home" className={linkCls}>
                Home
              </NavLink>

              <div
                className="relative"
                onMouseEnter={() => setDrop(true)}
                onMouseLeave={() => setDrop(false)}
              >
                <button
                  data-testid="nav-offerings-trigger"
                  onClick={() => setDrop((v) => !v)}
                  className={`flex items-center gap-1.5 text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                    drop ? "text-gold-300" : "text-zinc-400 hover:text-gold-200"
                  }`}
                >
                  Offerings
                  <ChevronDown
                    size={13}
                    className={`transition-transform duration-300 ${drop ? "rotate-180" : ""}`}
                  />
                </button>

                <AnimatePresence>
                  {drop && (
                    <motion.div
                      data-testid="nav-offerings-menu"
                      initial={{ opacity: 0, y: 10, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.98 }}
                      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-5 w-[340px]"
                    >
                      <div className="rounded-2xl bg-[#0b0b10]/[0.98] backdrop-blur-2xl border border-gold-400/20 p-2 shadow-[0_24px_60px_rgba(0,0,0,0.85)]">
                        {OFFERING_LINKS.map((item, i) => (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`nav-offering-${item.to.replace("/", "")}`}
                            className={({ isActive }) =>
                              `flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-300 ${
                                isActive ? "bg-gold-400/10" : "hover:bg-white/[0.05]"
                              }`
                            }
                          >
                            <span className="flex items-center gap-3">
                              <span className="font-mono text-[10px] text-gold-400/70">
                                0{i + 1}
                              </span>
                              <span className="text-sm font-medium text-zinc-200">{item.label}</span>
                            </span>
                            <ArrowUpRight size={14} className="text-gold-400/60" />
                          </NavLink>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {NAV_LINKS.filter((l) => l.to !== "/").map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.to.replace("/", "")}`}
                  className={linkCls}
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <Link to="/vault-trading" data-testid="nav-cta" className="btn-gold !hidden md:!inline-flex !px-5 !py-2.5">
                Explore Our Vaults
              </Link>
              <button
                data-testid="nav-mobile-toggle"
                onClick={() => setOpen((v) => !v)}
                className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-gold-400/25 text-gold-300"
                aria-label={open ? "Close menu" : "Open menu"}
              >
                {open ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile menu — outside <header> so the header's backdrop-filter cannot become its containing block */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden fixed inset-0 top-[76px] z-[60] bg-black/[0.97] backdrop-blur-2xl overflow-y-auto"
          >
            <div className="px-6 py-8 flex flex-col gap-8">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold-400/70 mb-4">
                  Offerings
                </p>
                <div className="flex flex-col">
                  {OFFERING_LINKS.map((item, i) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      data-testid={`nav-mobile-offering-${item.to.replace("/", "")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-4 py-3.5 border-b border-white/5 text-lg font-medium ${
                          isActive ? "text-gold-300" : "text-zinc-200"
                        }`
                      }
                    >
                      <span className="font-mono text-xs text-gold-400/60">0{i + 1}</span>
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold-400/70 mb-4">
                  Fund
                </p>
                <div className="flex flex-col">
                  {NAV_LINKS.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      data-testid={`nav-mobile-link-${l.to.replace("/", "") || "home"}`}
                      className={({ isActive }) =>
                        `py-3.5 border-b border-white/5 text-lg font-medium ${
                          isActive ? "text-gold-300" : "text-zinc-200"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </div>
              </div>
              <Link to="/vault-trading" data-testid="nav-mobile-cta" className="btn-gold w-full">
                Explore Our Vaults
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}